import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TrashIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import {
  BaseMaterialCatalogChips,
  BaseMaterialCatalogPicker
} from './baseMaterial/baseMaterialFields'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { minLockedCount, pipeRowBlockedMessage, resizeArrayByCount } from '../../utils/collectionSizing'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import { normalizeManufacturerLots } from '../../utils/normalizeManufacturerLots'
import EditingReturnBanner, { backToLabelFor } from '../projects/EditingReturnBanner'

type PipeEntry = {
  id: string
  description: string
  heatNumber: string
}

type ManufacturerLot = {
  id: string
  materialSpec: string[]
  typeGrade: string[]
  slabSource: string[]
  plateSource: string[]
  pipeManufacturer: string[]
  /** @deprecated use pipes[].heatNumber */
  heatNumbers?: string[]
  pipes: PipeEntry[]
}

export default function WpsBaseMaterial() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const { getProject } = useProjects()
  const { getSection, updateSection, error: sectionsError } = useWpsSections()
  // Slice 05 of pqr-preview-field-navigation. When the user clicks a
  // Material row on the PQR Preview canvas they're routed here with
  // `?focusPipe=N&returnTo=…`. The focus handler + back-button rewiring
  // below use this.
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [manufacturerLots, setManufacturerLots] = useState<ManufacturerLot[]>([])
  const [savedManufacturerLotsJson, setSavedManufacturerLotsJson] = useState('')
  const [pipeCountDraftByLot, setPipeCountDraftByLot] = useState<Record<string, string>>({})
  const [pipeCountError, setPipeCountError] = useState<{ lotId: string; message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const flatPipeIndexById = useMemo(() => {
    const map: Record<string, number> = {}
    let i = 0
    manufacturerLots.forEach((lot) => {
      lot.pipes.forEach((pipe) => {
        map[pipe.id] = i++
      })
    })
    return map
  }, [manufacturerLots])
  const totalPipes = useMemo(
    () => manufacturerLots.reduce((acc, l) => acc + l.pipes.length, 0),
    [manufacturerLots]
  )
  const pipeRowRefs = useRef<Record<number, HTMLDivElement | null>>({})
  useFieldFocusHandler({
    ready: totalPipes > 0,
    resolveTarget: (params) => {
      if (params.focusPipe === null) return null
      const idx = Number.parseInt(params.focusPipe, 10)
      if (!Number.isFinite(idx)) return null
      const wrapper = pipeRowRefs.current[idx]
      if (!wrapper) return null
      const focusable = wrapper.querySelector(
        'input, button, [role="button"], textarea, select'
      ) as HTMLElement | null
      return focusable ?? wrapper
    }
  })
  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : { onClick: () => navigate(-1), label: 'Back' }

  const commitLoadedLots = useCallback((lots: ManufacturerLot[]) => {
    setManufacturerLots(lots)
    setSavedManufacturerLotsJson(JSON.stringify(lots))
    setPipeCountDraftByLot(
      lots.reduce<Record<string, string>>((acc, lot) => {
        acc[lot.id] = String(lot.pipes.length)
        return acc
      }, {})
    )
  }, [])

  const lotLengthSignature = useMemo(
    () => manufacturerLots.map((l) => `${l.id}:${l.pipes.length}`).join('|'),
    [manufacturerLots]
  )

  const isDirty = useMemo(() => {
    if (!savedManufacturerLotsJson) {
      return manufacturerLots.length > 0 && JSON.stringify(manufacturerLots) !== '[]'
    }
    return JSON.stringify(manufacturerLots) !== savedManufacturerLotsJson
  }, [manufacturerLots, savedManufacturerLotsJson])

  const formatRange = (value: string, delta: number) => {
    const numeric = Number(value)
    if (Number.isNaN(numeric)) return 'Range TBD'
    const min = Math.max(0, numeric - delta)
    const max = numeric + delta
    const precision = value.includes('.') ? value.split('.')[1].length : 0
    const fixed = Math.max(precision, delta.toString().includes('.') ? delta.toString().split('.')[1].length : 0)
    return `${min.toFixed(fixed)} – ${max.toFixed(fixed)}`
  }

  useEffect(() => {
    setSavedManufacturerLotsJson('')
  }, [pipeId])

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('base')
    if (parsed) {
      try {
        if (parsed.manufacturerLots) {
          const lots = normalizeManufacturerLots(parsed.manufacturerLots) as ManufacturerLot[]
          commitLoadedLots(lots)
        } else if (
          parsed.materialSpec ||
          parsed.typeGrade ||
          parsed.slabSource ||
          parsed.plateSource ||
          parsed.pipeManufacturer
        ) {
          commitLoadedLots([
            {
              id: `lot-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
              materialSpec: parsed.materialSpec || [],
              typeGrade: parsed.typeGrade || [],
              slabSource: parsed.slabSource || [],
              plateSource: parsed.plateSource || [],
              pipeManufacturer: parsed.pipeManufacturer || [],
              heatNumbers: parsed.heatNumbers || [],
              pipes: (parsed.heatNumbers || []).map((hn: string, i: number) => ({
                id: `pipe-0-${i}-${Date.now()}`,
                description: '',
                heatNumber: hn
              }))
            }
          ])
        } else {
          commitLoadedLots([])
        }
        return
      } catch (error) {
        // ignore
      }
    }
    if (pipeSet) {
    }
  }, [pipeId, pipeSet, getSection, commitLoadedLots])

  useEffect(() => {
    setPipeCountDraftByLot((prev) => {
      const next = { ...prev }
      let changed = false
      manufacturerLots.forEach((lot) => {
        if (pipeCountError?.lotId === lot.id) return
        const desired = String(lot.pipes.length)
        if (next[lot.id] !== desired) {
          next[lot.id] = desired
          changed = true
        }
      })
      Object.keys(next).forEach((id) => {
        if (!manufacturerLots.some((l) => l.id === id)) {
          delete next[id]
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [lotLengthSignature, manufacturerLots, pipeCountError])

  useEffect(() => {
    if (!pipeCountError) return
    const { lotId } = pipeCountError
    const onPointerDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement
      if (el.closest(`[data-lot-pipe-count="${lotId}"]`)) return
      setPipeCountError(null)
      const lot = manufacturerLots.find((l) => l.id === lotId)
      if (lot) {
        setPipeCountDraftByLot((prev) => ({ ...prev, [lotId]: String(lot.pipes.length) }))
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [pipeCountError, manufacturerLots])

  const pipeOccupied = (p: PipeEntry) =>
    Boolean(p.description?.trim()) || Boolean(p.heatNumber?.trim())

  const canRemovePipeRow = (lot: ManufacturerLot, pipe: PipeEntry) => {
    const remaining = lot.pipes.filter((p) => p.id !== pipe.id)
    return remaining.length >= minLockedCount(remaining, pipeOccupied)
  }

  const removePipeRow = (lotId: string, pipeId: string) => {
    setPipeCountError(null)
    setManufacturerLots((prev) =>
      prev.map((item) => {
        if (item.id !== lotId) return item
        const pipe = item.pipes.find((p) => p.id === pipeId)
        if (!pipe || !canRemovePipeRow(item, pipe)) return item
        return { ...item, pipes: item.pipes.filter((p) => p.id !== pipeId) }
      })
    )
  }

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    setSaving(true)
    setSaveError(null)
    try {
      await updateSection('base', { manufacturerLots })
      setSavedManufacturerLotsJson(JSON.stringify(manufacturerLots))
      toast.success('Base material saved')
      if (returnTo) {
        navigate(returnTo)
      } else {
        navigate(-1)
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to save base material'
      setSaveError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const displayedSaveError = saveError || sectionsError

  if (!project || !pipeSet) {
    return (
      <Card title="Base Material not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={returnTo} contextLabel="Base Material" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={
          <>
            <span>Base Material</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving} loading={saving} onClick={handleSave}>
              Save
            </Button>
          </>
        }
      />

      {displayedSaveError ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {displayedSaveError}
        </div>
      ) : null}

      <div className="min-w-0 space-y-6 border-0 p-0">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] font-medium text-gray-500">Nominal Diameter</p>
          <p className="mt-1 text-sm font-semibold text-gray-900">
            {pipeSet.nominalOd} {pipeSet.nominalOdUnit}
          </p>
          <p className="mt-1 text-[11px] text-gray-400">
            Range: {formatRange(pipeSet.nominalOd, 0.125)} {pipeSet.nominalOdUnit}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] font-medium text-gray-500">Nominal Wall Thickness</p>
          <p className="mt-1 text-sm font-semibold text-gray-900">
            {pipeSet.wallThickness} {pipeSet.wallThicknessUnit}
          </p>
          <p className="mt-1 text-[11px] text-gray-400">
            Range: {formatRange(pipeSet.wallThickness, 0.0125)} {pipeSet.wallThicknessUnit}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-gray-700">Manufacturer Lots</p>
            <p className="mt-1 text-[11px] text-gray-500">
              Add one card per manufacturer and capture heat numbers.
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setManufacturerLots((prev) => [
                ...prev,
                {
                  id: `lot-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
                  materialSpec: [],
                  typeGrade: [],
                  slabSource: [],
                  plateSource: [],
                  pipeManufacturer: [],
                  heatNumbers: [],
                  pipes: []
                }
              ])
            }}
          >
            Add Manufacturer
          </Button>
        </div>
        {manufacturerLots.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
            No manufacturer lots yet.
          </div>
        ) : (
          <div className="mt-4 grid gap-4">
            {manufacturerLots.map((lot, index) => (
              <div key={lot.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-gray-700">
                    Manufacturer {index + 1}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setManufacturerLots((prev) => prev.filter((item) => item.id !== lot.id))
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                  <BaseMaterialChipsField
                    label="Material Specification"
                    values={lot.materialSpec}
                    categoryKey="base_material_spec"
                    placeholder="Select material spec..."
                    addLabel="Add Material Spec"
                    onChange={(values) =>
                      setManufacturerLots((prev) =>
                        prev.map((item) =>
                          item.id === lot.id ? { ...item, materialSpec: values } : item
                        )
                      )
                    }
                  />
                  <BaseMaterialChipsField
                    label="Type / Grade"
                    values={lot.typeGrade}
                    categoryKey="base_type_grade"
                    placeholder="Select type or grade..."
                    addLabel="Add Type / Grade"
                    onChange={(values) =>
                      setManufacturerLots((prev) =>
                        prev.map((item) =>
                          item.id === lot.id ? { ...item, typeGrade: values } : item
                        )
                      )
                    }
                  />
                  <BaseMaterialChipsField
                    label="Pipe Manufacturer"
                    values={lot.pipeManufacturer}
                    categoryKey="base_manufacturer"
                    placeholder="Select pipe manufacturer..."
                    addLabel="Add Manufacturer"
                    onChange={(values) =>
                      setManufacturerLots((prev) =>
                        prev.map((item) =>
                          item.id === lot.id ? { ...item, pipeManufacturer: values } : item
                        )
                      )
                    }
                  />
                  <BaseMaterialChipsField
                    label="Slab Source"
                    values={lot.slabSource}
                    categoryKey="base_source_mill"
                    placeholder="Select slab source..."
                    addLabel="Add Slab Source"
                    onChange={(values) =>
                      setManufacturerLots((prev) =>
                        prev.map((item) =>
                          item.id === lot.id ? { ...item, slabSource: values } : item
                        )
                      )
                    }
                  />
                  <BaseMaterialChipsField
                    label="Plate Source"
                    values={lot.plateSource}
                    categoryKey="base_source_mill"
                    placeholder="Select plate source..."
                    addLabel="Add Plate Source"
                    onChange={(values) =>
                      setManufacturerLots((prev) =>
                        prev.map((item) =>
                          item.id === lot.id ? { ...item, plateSource: values } : item
                        )
                      )
                    }
                  />
                </div>

                <div className="mt-4 border-t border-gray-200 pt-4">
                  <label className="text-xs font-medium text-gray-500">Number of pipes</label>
                  <input
                    data-lot-pipe-count={lot.id}
                    type="number"
                    min={0}
                    className="input-primary mt-1 w-24"
                    value={pipeCountDraftByLot[lot.id] ?? String(lot.pipes.length)}
                    onChange={(e) => {
                      const raw = e.target.value
                      setPipeCountDraftByLot((prev) => ({ ...prev, [lot.id]: raw }))
                      if (raw.trim() === '') return
                      const n = Math.max(0, parseInt(raw, 10) || 0)
                      setPipeCountError(null)
                      setManufacturerLots((prev) =>
                        prev.map((item) => {
                          if (item.id !== lot.id) return item
                          const current = item.pipes
                          const emptyPipe = (): PipeEntry => ({
                            id: `pipe-${lot.id}-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
                            description: '',
                            heatNumber: ''
                          })
                          const res = resizeArrayByCount(current, n, emptyPipe, pipeOccupied, {
                            blockedShrinkMessage: pipeRowBlockedMessage
                          })
                          if (!res.ok) {
                            setPipeCountError({ lotId: lot.id, message: res.error })
                            return item
                          }
                          return { ...item, pipes: res.items }
                        })
                      )
                    }}
                    onBlur={() => {
                      setPipeCountError((err) => (err?.lotId === lot.id ? null : err))
                      setPipeCountDraftByLot((prev) => ({
                        ...prev,
                        [lot.id]: String(lot.pipes.length)
                      }))
                    }}
                  />
                  {pipeCountError != null && pipeCountError.lotId === lot.id && (
                    <p className="mt-2 text-xs text-error-600">{pipeCountError.message}</p>
                  )}
                  {lot.pipes.length > 0 && (
                    <div className="mt-3 space-y-3">
                      <p className="text-[11px] font-medium text-gray-500">Pipes</p>
                      {lot.pipes.map((pipe, pipeIndex) => (
                        <div
                          key={pipe.id}
                          ref={(el) => {
                            const idx = flatPipeIndexById[pipe.id]
                            if (idx !== undefined) pipeRowRefs.current[idx] = el
                          }}
                          data-focus-pipe={flatPipeIndexById[pipe.id]}
                          className="flex flex-col gap-2 rounded border border-gray-100 bg-white p-3 sm:flex-row sm:items-start"
                        >
                          <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="text-[11px] font-medium text-gray-500">
                              Pipe {pipeIndex + 1} – Description
                            </label>
                            <input
                              type="text"
                              className="input-primary mt-1 w-full"
                              placeholder="Description"
                              value={pipe.description}
                              onChange={(e) =>
                                setManufacturerLots((prev) =>
                                  prev.map((item) => {
                                    if (item.id !== lot.id) return item
                                    return {
                                      ...item,
                                      pipes: item.pipes.map((p) =>
                                        p.id === pipe.id ? { ...p, description: e.target.value } : p
                                      )
                                    }
                                  })
                                )
                              }
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-medium text-gray-500">
                              Pipe {pipeIndex + 1} – Heat number
                            </label>
                            <BaseMaterialCatalogPicker
                              value={pipe.heatNumber}
                              categoryKey="base_heat_number"
                              placeholder="Select heat number..."
                              addLabel="Add Heat Number"
                              className="mt-1"
                              scopedProjectId={id}
                              onChange={(value) =>
                                setManufacturerLots((prev) =>
                                  prev.map((item) => {
                                    if (item.id !== lot.id) return item
                                    return {
                                      ...item,
                                      pipes: item.pipes.map((p) =>
                                        p.id === pipe.id ? { ...p, heatNumber: value } : p
                                      )
                                    }
                                  })
                                )
                              }
                            />
                          </div>
                          </div>
                          <button
                            type="button"
                            className={clsx(
                              'inline-flex shrink-0 items-center justify-center rounded-md p-2 text-error-600 transition hover:bg-error-50',
                              !canRemovePipeRow(lot, pipe) && 'cursor-not-allowed opacity-40'
                            )}
                            disabled={!canRemovePipeRow(lot, pipe)}
                            title={
                              !canRemovePipeRow(lot, pipe)
                                ? 'Clear this pipe row to remove it, or lower the pipe count.'
                                : 'Remove this pipe'
                            }
                            aria-label={`Remove pipe ${pipeIndex + 1}`}
                            onClick={() => removePipeRow(lot.id, pipe.id)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  )
}

interface BaseMaterialChipsFieldProps {
  label: string
  values: string[]
  categoryKey: React.ComponentProps<typeof BaseMaterialCatalogChips>['categoryKey']
  placeholder: string
  addLabel: string
  onChange: (values: string[]) => void
}

function BaseMaterialChipsField({
  label,
  values,
  categoryKey,
  placeholder,
  addLabel,
  onChange
}: BaseMaterialChipsFieldProps) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <BaseMaterialCatalogChips
        values={values}
        categoryKey={categoryKey}
        placeholder={placeholder}
        addLabel={addLabel}
        onChange={onChange}
        className="mt-1"
      />
    </div>
  )
}
